/**
 * 外观运行时应用器（任务 14.31c）：设置页改值 / system 模式下系统主题变化时，
 * 复用 appearanceDomPlan() 把设置落到 DOM——与 index.html 内联脚本（首屏前
 * 冷启动路径）同语义，这里管运行时切换路径。
 *
 * 形态：主线程模块级单例（设备级设置，与账号无关，不进 GlobalState——
 * 订阅方只有外观设置页自身；气泡字号等全靠 CSS 变量生效无需组件重渲染）。
 * 依赖全部可注入（storage/matchMedia/DOM 落点），纯 node 环境可单测。
 */
import {
  APPEARANCE_STORAGE_KEY,
  appearanceDomPlan,
  normalizeAppearance,
  readAppearance,
  type AppearanceDomPlan,
  type AppearanceSettings,
} from './appearanceModel';

/** DOM 落点最小面（documentElement + meta[name=theme-color]），可注入假实现单测 */
export interface AppearanceDomTarget {
  setAttr(name: string, value: string): void;
  removeAttr(name: string): void;
  setStyleVar(name: string, value: string): void;
  removeStyleVar(name: string): void;
  setThemeColor(value: string): void;
}

/** 计划 → DOM（null = 移除回落 :root 默认；index.html 内联脚本同语义） */
export function applyAppearancePlan(plan: AppearanceDomPlan, dom: AppearanceDomTarget): void {
  if (plan.theme !== null) dom.setAttr('data-theme', plan.theme);
  else dom.removeAttr('data-theme');
  if (plan.accent !== null) dom.setAttr('data-accent', plan.accent);
  else dom.removeAttr('data-accent');
  if (plan.chatBg !== null) dom.setAttr('data-chat-bg', plan.chatBg);
  else dom.removeAttr('data-chat-bg');
  if (plan.fontScale !== null) dom.setStyleVar('--font-scale', plan.fontScale);
  else dom.removeStyleVar('--font-scale');
  dom.setThemeColor(plan.themeColor);
}

/** prefers-color-scheme: dark 查询的最小面（真 MediaQueryList 可直接传入） */
export interface SystemDarkQuery {
  readonly matches: boolean;
  addEventListener(type: 'change', listener: () => void): void;
}

export interface AppearanceRuntimeDeps {
  storage: Pick<Storage, 'getItem' | 'setItem'>;
  dom: AppearanceDomTarget;
  systemDark: SystemDarkQuery;
}

export interface AppearanceRuntime {
  get(): AppearanceSettings;
  /** 归并改值：normalize → 持久化 → 落 DOM → 通知订阅方 */
  update(patch: Partial<AppearanceSettings>): void;
  subscribe(fn: () => void): () => void;
}

export function createAppearanceRuntime(deps: AppearanceRuntimeDeps): AppearanceRuntime {
  let settings = readAppearance(deps.storage);
  const listeners = new Set<() => void>();

  const apply = (): void => {
    applyAppearancePlan(appearanceDomPlan(settings, deps.systemDark.matches), deps.dom);
  };

  // 冷启动 index.html 已落过一次；这里再应用一次幂等找平（SPA 生命周期内
  // 若系统主题已变、或内联脚本与模型版本错位，以模型为准）
  apply();

  // system 模式跟随系统明暗切换（light/dark 模式下系统变化不影响结果，
  // 但 plan 是纯函数重算，照常应用也幂等）
  deps.systemDark.addEventListener('change', () => {
    apply();
    listeners.forEach((fn) => fn());
  });

  return {
    get: () => settings,
    update(patch) {
      settings = normalizeAppearance({ ...settings, ...patch });
      try {
        deps.storage.setItem(APPEARANCE_STORAGE_KEY, JSON.stringify(settings));
      } catch {
        // 写失败仅本次会话生效，不炸页面
      }
      apply();
      listeners.forEach((fn) => fn());
    },
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
  };
}

/** 浏览器真实落点 */
function browserDomTarget(): AppearanceDomTarget {
  const root = document.documentElement;
  return {
    setAttr: (n, v) => root.setAttribute(n, v),
    removeAttr: (n) => root.removeAttribute(n),
    setStyleVar: (n, v) => root.style.setProperty(n, v),
    removeStyleVar: (n) => root.style.removeProperty(n),
    setThemeColor: (v) => {
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', v);
    },
  };
}

let singleton: AppearanceRuntime | null = null;

/** 主线程单例（main.tsx 启动时调一次建起 system 监听；设置页复用同一实例） */
export function appearanceRuntime(): AppearanceRuntime {
  if (singleton === null) {
    singleton = createAppearanceRuntime({
      storage: localStorage,
      dom: browserDomTarget(),
      systemDark: matchMedia('(prefers-color-scheme: dark)'),
    });
  }
  return singleton;
}
